import {Capacitor} from '@capacitor/core'
import {ActionPerformed, PushNotifications, PushNotificationSchema, Token} from '@capacitor/push-notifications'
import {InjectionKey} from 'vue'
import _ from 'lodash'
import Ajv, {JSONSchemaType} from 'ajv'
import {Subscription} from '@/service/subscription/Subscription'
import {BadgeService} from '@/service/BadgeService'

export type DeviceTokenChangedCallback = (deviceToken: string) => void | Promise<void>

export interface RegisterForCallbackParams<T> {
  event: string
  callback: (event: string, data: T) => void | Promise<void>
  schema: JSONSchemaType<T>
}

class EventCallbackSubscription<T> implements Subscription {
  constructor(
    private readonly pushNotificationService: PushNotificationService,
    readonly event: string,
    readonly callback: (event: string, data: T) => void | Promise<void>,
    readonly dataSchema?: JSONSchemaType<T>
  ) {
  }
  
  call(ajv: Ajv, data: any) {
    const event = data.event
    if (event !== this.event) {
      return
    }
    
    if (this.dataSchema) {
      const validate = ajv.compile(this.dataSchema)
      if (!validate(data)) {
        console.error('Ignoring notification \'%o\' because content is not matching expected schema: %o', event, data)
        return
      }
    }
    
    this.callback(event, data)
  }
  
  unregister() {
    this.pushNotificationService.unregister(this)
  }
}

export class PushNotificationService {
  private registering = false
  private retryCount = 0
  private deviceToken?: string
  private registrationError?: any
  private deviceTokenChangedCallback?: DeviceTokenChangedCallback
  
  private static readonly MAX_REGISTRATION_RETRIES = 3
  private static readonly REGISTRATION_RETRY_DELAY_MS = 5000
  
  private readonly ajv: Ajv
  private readonly badgeService: BadgeService
  private readonly eventReceivedSubscriptions: EventCallbackSubscription<unknown>[] = []
  private readonly eventActionSubscriptions: EventCallbackSubscription<unknown>[] = []
  private pendingActionData: any[] = []
  
  constructor() {
    this.ajv = new Ajv()
    this.badgeService = new BadgeService()
    if (Capacitor.getPlatform() === 'web') {
      return
    }
    PushNotifications.addListener('registration', this.onRegistration.bind(this))
    PushNotifications.addListener('registrationError', this.onRegistrationFailed.bind(this))
    PushNotifications.addListener('pushNotificationReceived', this.onPushNotificationReceived.bind(this))
    PushNotifications.addListener('pushNotificationActionPerformed', this.onPushNotificationActionPerformed.bind(this))
  }
  
  async register(): Promise<void> {
    if (Capacitor.getPlatform() === 'web') {
      return
    }
    if (this.registering || this.deviceToken) {
      return
    }
    this.registering = true
    
    console.info('Registering to push notifications.')
    this.deviceToken = undefined
    this.registrationError = undefined
    const permissions = await PushNotifications.checkPermissions()
    if (permissions.receive === 'granted') {
      try {
        await PushNotifications.register()
      } catch (error) {
        console.error('PushNotifications.register() rejected.', error)
        this.registering = false
        this.scheduleRetry()
      }
    } else {
      console.info('Push notification permission not granted yet (state: %s). Skipping registration.', permissions.receive)
      this.registering = false
    }
  }
  
  getDeviceToken(): string | undefined {
    return this.deviceToken
  }
  
  setDeviceTokenChangedCallback(callback?: DeviceTokenChangedCallback) {
    this.deviceTokenChangedCallback = callback
  }
  
  registerForEventReceived<T>(params: RegisterForCallbackParams<T>): Subscription {
    const subscription = new EventCallbackSubscription<T>(
      this,
      params.event,
      params.callback,
      params.schema
    )
    this.eventReceivedSubscriptions.push(subscription as EventCallbackSubscription<unknown>)
    return subscription
  }
  
  registerForEventAction<T>(params: RegisterForCallbackParams<T>): Subscription {
    const subscription = new EventCallbackSubscription<T>(
      this,
      params.event,
      params.callback,
      params.schema
    )
    this.eventActionSubscriptions.push(subscription as EventCallbackSubscription<unknown>)
    this.replayPendingActions()
    return subscription
  }
  
  unregister(subscription: Subscription) {
    _.remove(this.eventReceivedSubscriptions, it => it === subscription)
    _.remove(this.eventActionSubscriptions, it => it === subscription)
  }
  
  private onRegistration(token: Token) {
    this.registering = false
    this.retryCount = 0
    this.deviceToken = token.value
    
    console.log(`Registration to push notifications completed with token: ${token.value}`)
    
    const callback = this.deviceTokenChangedCallback
    if (callback) {
      callback(token.value)
    }
  }
  
  private onRegistrationFailed(error: any) {
    console.error('Failed to register to push notifications.', error)
    
    this.registrationError = error
    this.registering = false
    this.scheduleRetry()
  }
  
  private scheduleRetry() {
    if (this.retryCount >= PushNotificationService.MAX_REGISTRATION_RETRIES) {
      console.error(`Push notification registration failed after ${this.retryCount} retries. Giving up.`)
      return
    }
    this.retryCount++
    const delay = PushNotificationService.REGISTRATION_RETRY_DELAY_MS * this.retryCount
    console.info(`Scheduling push notification registration retry ${this.retryCount}/${PushNotificationService.MAX_REGISTRATION_RETRIES} in ${delay}ms.`)
    setTimeout(() => {
      this.register().catch(e => console.error('Push notification registration retry failed.', e))
    }, delay)
  }
  
  private onPushNotificationReceived(notification: PushNotificationSchema) {
    const event = notification.data.event
    if (event) {
      console.log(`Push notification received with event '${event}'`)
      
      this.badgeService.increase()
        .catch(e => console.warn('Failed to increase badge on notification received.', e))
      
      const subscriptions = _.filter(this.eventReceivedSubscriptions, it => it.event === event)
      for (const subscription of subscriptions) {
        subscription.call(this.ajv, notification.data)
      }
    }
  }
  
  private onPushNotificationActionPerformed(action: ActionPerformed) {
    const event = action.notification.data.event
    
    if (event) {
      console.log(`Push notification action performed with event '${event}'`)
      
      const subscriptions = _.filter(this.eventActionSubscriptions, it => it.event === event)
      if (subscriptions.length === 0) {
        console.info(`No subscriber for event '${event}' yet, buffering action for replay.`)
        this.pendingActionData.push(action.notification.data)
        return
      }
      for (const subscription of subscriptions) {
        subscription.call(this.ajv, action.notification.data)
      }
    }
  }
  
  private replayPendingActions() {
    if (this.pendingActionData.length === 0) {
      return
    }
    const stillPending: any[] = []
    for (const data of this.pendingActionData) {
      const event = data.event
      if (!event) {
        continue
      }
      const subscriptions = _.filter(this.eventActionSubscriptions, it => it.event === event)
      if (subscriptions.length > 0) {
        console.info(`Replaying buffered push notification action for event '${event}'`)
        for (const subscription of subscriptions) {
          subscription.call(this.ajv, data)
        }
      } else {
        stillPending.push(data)
      }
    }
    this.pendingActionData = stillPending
  }
  getBadgeService(): BadgeService {
    return this.badgeService
  }
}

export const pushNotificationServiceKey: InjectionKey<PushNotificationService> = Symbol()
